import "./NewAddress.scss";
import { useEffect, useState, useRef } from "react";
import addressData from "../../../../../../../../Data/ph-address-2019v2.json";
import { useAppContext } from "../../../../../../../../Components/AppProvider";

function NewAddress({ onClose, user_id, onAddressAdded, hasAddressAlready }) {
  const regionListRef = useRef(null);
  const provinceListRef = useRef(null);
  const cityListRef = useRef(null);
  const brgyListRef = useRef(null);

  const { hasNewAddress, setHasNewAddress } = useAppContext();
  const [isRegionFocused, setIsRegionFocused] = useState(false);
  const [isProvinceFocused, setIsProvinceFocused] = useState(false);
  const [isCityFocused, setIsCityFocused] = useState(false);
  const [isBrgyFocused, setIsBrgyFocused] = useState(false);

  const [regionValue, setRegionValue] = useState("");
  const [provinceValue, setProvinceValue] = useState("");
  const [cityValue, setCityValue] = useState("");
  const [brgyValue, setBrgyValue] = useState("");
  const [fullName, setFullName] = useState("");
  const [contact, setContact] = useState("");
  const [street, setStreet] = useState("");
  const [landmark, setLandmark] = useState("");
  const [defaultAddress, setDefaultAddress] = useState(false);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const regionRef = useRef(null);
  const provinceRef = useRef(null);
  const cityRef = useRef(null);
  const brgyRef = useRef(null);

  // highlight index
  const [highlightedRegionIndex, setHighlightedRegionIndex] = useState(-1);
  const [highlightedProvinceIndex, setHighlightedProvinceIndex] = useState(-1);
  const [highlightedCityIndex, setHighlightedCityIndex] = useState(-1);
  const [highlightedBrgyIndex, setHighlightedBrgyIndex] = useState(-1);

  // region conversion
  const regionsArray = Object.entries(addressData).map(([code, data]) => ({
    code,
    name: data.region_name,
    provinces: data.province_list,
  }));

  // helper to prevent .toLowerCase() crashes
  const safeIncludes = (item, search) =>
    (item?.toString?.().toLowerCase?.() || "").includes(
      search?.toString?.().toLowerCase?.() || ""
    );

  const filteredRegions = regionsArray.filter((r) =>
    safeIncludes(r?.name, regionValue)
  );

  const matchedRegion = regionsArray.find((r) =>
    safeIncludes(r?.name, regionValue.trim())
  );

  const filteredProvinces = matchedRegion
    ? Object.keys(matchedRegion.provinces || {}).filter((p) =>
        safeIncludes(p, provinceValue)
      )
    : [];

  const matchedProvinces = matchedRegion
    ? Object.keys(matchedRegion.provinces || {}).find((p) =>
        safeIncludes(p, provinceValue.trim())
      )
    : null;

  const filteredCities =
    matchedProvinces && matchedRegion
      ? Object.keys(
          matchedRegion.provinces[matchedProvinces]?.municipality_list || {}
        ).filter((c) => safeIncludes(c, cityValue))
      : [];

  const matchedCities =
    matchedProvinces && matchedRegion
      ? Object.keys(
          matchedRegion.provinces[matchedProvinces]?.municipality_list || {}
        ).find((c) => safeIncludes(c, cityValue.trim()))
      : null;

  const filteredBarangays =
    matchedCities && matchedRegion
      ? matchedRegion.provinces[matchedProvinces]?.municipality_list[
          matchedCities
        ]?.barangay_list?.filter((b) => safeIncludes(b, brgyValue)) || []
      : [];

  // validation
  const validateForm = () => {
    let newErrors = {};
    if (!fullName.trim()) newErrors.fullName = "Full Name is required";
    if (!contact.trim()) newErrors.contact = "Contact is required";
    if (!regionValue.trim()) newErrors.region = "Region is required";
    if (!provinceValue.trim()) newErrors.province = "Province is required";
    if (!cityValue.trim()) newErrors.city = "City/Municipality is required";
    if (!brgyValue.trim()) newErrors.brgy = "Barangay is required";
    if (!street.trim()) newErrors.street = "Street/House No. is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    if (!hasAddressAlready) setDefaultAddress(true);
  }, [hasAddressAlready]);

  const handleSave = async () => {
    setSaving(true);
    if (validateForm()) {
      const isDefault = hasAddressAlready ? (defaultAddress ? 1 : 0) : 1;
      const json = JSON.stringify({
        cpId: user_id,
        fullName,
        contact,
        region: regionValue,
        province: provinceValue,
        city: cityValue,
        barangay: brgyValue,
        street,
        landmark,
        isDefault,
      });
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BASE_URL}api/address/add-new-address`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: json,
          }
        );

        const data = await res.json();
        if (!res.ok) {
          alert(`Error: ${data.error}`);
          return;
        }
        onAddressAdded();
        setHasNewAddress((p) => !p);
        onClose();
      } catch (err) {
        alert("Internal server error.");
      }
    }
    setSaving(false);
  };

  // keyboard handler with Tab support
  const handleKeyDown = (
    e,
    list,
    setValue,
    setHighlight,
    setFocus,
    nextRef
  ) => {
    if (!list || !list.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((prev) => (prev < list.length - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((prev) => (prev > 0 ? prev - 1 : list.length - 1));
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      const index = e.key === "Tab" ? 0 : e.target.dataset.highlight;
      const val = list[index] || list[0];
      if (val) {
        setValue(val);
        setFocus(false);
        if (nextRef && nextRef.current) {
          nextRef.current.focus();
        }
      }
    } else if (e.key === "Escape") {
      setFocus(false);
    }
  };
  useEffect(() => {
    const scrollIntoViewIfNeeded = (containerRef, index) => {
      if (!containerRef.current) return;
      const items = containerRef.current.querySelectorAll(".keyword-result");
      const focused = items[index];
      if (focused) {
        focused.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    };

    scrollIntoViewIfNeeded(regionListRef, highlightedRegionIndex);
  }, [highlightedRegionIndex]);

  useEffect(() => {
    const scrollIntoViewIfNeeded = (containerRef, index) => {
      if (!containerRef.current) return;
      const items = containerRef.current.querySelectorAll(".keyword-result");
      const focused = items[index];
      if (focused) {
        focused.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    };

    scrollIntoViewIfNeeded(provinceListRef, highlightedProvinceIndex);
  }, [highlightedProvinceIndex]);

  useEffect(() => {
    const scrollIntoViewIfNeeded = (containerRef, index) => {
      if (!containerRef.current) return;
      const items = containerRef.current.querySelectorAll(".keyword-result");
      const focused = items[index];
      if (focused) {
        focused.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    };

    scrollIntoViewIfNeeded(cityListRef, highlightedCityIndex);
  }, [highlightedCityIndex]);

  useEffect(() => {
    const scrollIntoViewIfNeeded = (containerRef, index) => {
      if (!containerRef.current) return;
      const items = containerRef.current.querySelectorAll(".keyword-result");
      const focused = items[index];
      if (focused) {
        focused.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    };

    scrollIntoViewIfNeeded(brgyListRef, highlightedBrgyIndex);
  }, [highlightedBrgyIndex]);

  return (
    <div className="na-modal-content-container">
      {/* Full name */}
      <div className="nmc-input-wrapper">
        <label>Full Name :</label>
        <input
          type="text"
          value={fullName}
          onChange={(e) => {
            setFullName(e.target.value);
            setErrors((p) => ({ ...p, fullName: "" }));
          }}
          style={{ borderColor: errors.fullName ? "#da4f4f" : "black" }}
        />
        {errors.fullName && <span className="error">{errors.fullName}</span>}
      </div>

      {/* Contact */}
      <div className="nmc-input-wrapper">
        <label>Contact :</label>
        <input
          type="text"
          value={contact}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, "").slice(0, 11);
            setContact(digits);
            setErrors((p) => ({ ...p, contact: "" }));
          }}
          style={{ borderColor: errors.contact ? "#da4f4f" : "" }}
        />
        {errors.contact && <span className="error">{errors.contact}</span>}
      </div>

      {/* Region */}
      <div className="nmc-input-wrapper">
        <label>Region :</label>
        <input
          ref={regionRef}
          type="text"
          value={regionValue}
          data-highlight={highlightedRegionIndex}
          onChange={(e) => {
            setRegionValue(e.target.value);
            setErrors((p) => ({ ...p, region: "" }));
            setProvinceValue("");
            setCityValue("");
            setBrgyValue("");
            setHighlightedRegionIndex(-1);
          }}
          onFocus={() => setIsRegionFocused(true)}
          onBlur={() => setIsRegionFocused(false)}
          onKeyDown={(e) =>
            handleKeyDown(
              e,
              filteredRegions.map((r) => r.name),
              (val) => {
                setRegionValue(val);
                setProvinceValue("");
                setCityValue("");
                setBrgyValue("");
              },
              setHighlightedRegionIndex,
              setIsRegionFocused,
              provinceRef
            )
          }
          style={{ borderColor: errors.region ? "#da4f4f" : "" }}
        />
        {isRegionFocused && (
          <div className="nmc-keyword-result" ref={regionListRef}>
            {filteredRegions.map((r, idx) => (
              <div
                key={r.code}
                className={`keyword-result ${
                  idx === highlightedRegionIndex ? "highlighted" : ""
                }`}
                onMouseDown={() => {
                  setRegionValue(r.name);
                  setProvinceValue("");
                  setCityValue("");
                  setBrgyValue("");
                }}
              >
                {r.name}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Province */}
      <div className="nmc-input-wrapper">
        <label>Province/District :</label>
        <input
          ref={provinceRef}
          type="text"
          disabled={!matchedRegion}
          value={provinceValue}
          data-highlight={highlightedProvinceIndex}
          onChange={(e) => {
            setProvinceValue(e.target.value);
            setCityValue("");
            setBrgyValue("");
            setErrors((p) => ({ ...p, province: "" }));
            setHighlightedProvinceIndex(-1);
          }}
          onFocus={() => setIsProvinceFocused(true)}
          onBlur={() => setIsProvinceFocused(false)}
          onKeyDown={(e) =>
            handleKeyDown(
              e,
              filteredProvinces,
              (val) => {
                setProvinceValue(val);
                setCityValue("");
                setBrgyValue("");
              },
              setHighlightedProvinceIndex,
              setIsProvinceFocused,
              cityRef
            )
          }
          style={{ borderColor: errors.province ? "#da4f4f" : "" }}
        />
        {isProvinceFocused && (
          <div className="nmc-keyword-result" ref={provinceListRef}>
            {filteredProvinces.map((p, idx) => (
              <div
                key={p}
                className={`keyword-result ${
                  idx === highlightedProvinceIndex ? "highlighted" : ""
                }`}
                onMouseDown={() => {
                  setProvinceValue(p);
                  setCityValue("");
                  setBrgyValue("");
                }}
              >
                {p}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* City */}
      <div className="nmc-input-wrapper">
        <label>City/Municipal :</label>
        <input
          ref={cityRef}
          type="text"
          disabled={!matchedProvinces}
          value={cityValue}
          data-highlight={highlightedCityIndex}
          onChange={(e) => {
            setCityValue(e.target.value);
            setBrgyValue("");
            setErrors((p) => ({ ...p, city: "" }));
            setHighlightedCityIndex(-1);
          }}
          onFocus={() => setIsCityFocused(true)}
          onBlur={() => setIsCityFocused(false)}
          onKeyDown={(e) =>
            handleKeyDown(
              e,
              filteredCities,
              (val) => {
                setCityValue(val);
                setBrgyValue("");
              },
              setHighlightedCityIndex,
              setIsCityFocused,
              brgyRef
            )
          }
          style={{ borderColor: errors.city ? "#da4f4f" : "" }}
        />
        {isCityFocused && (
          <div className="nmc-keyword-result" ref={cityListRef}>
            {filteredCities.map((c, idx) => (
              <div
                key={c}
                className={`keyword-result ${
                  idx === highlightedCityIndex ? "highlighted" : ""
                }`}
                onMouseDown={() => {
                  setCityValue(c);
                  setBrgyValue("");
                }}
              >
                {c}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Barangay */}
      <div className="nmc-input-wrapper">
        <label>Barangay :</label>
        <input
          ref={brgyRef}
          type="text"
          disabled={!matchedCities}
          value={brgyValue}
          data-highlight={highlightedBrgyIndex}
          onChange={(e) => {
            setBrgyValue(e.target.value);
            setErrors((p) => ({ ...p, brgy: "" }));
            setHighlightedBrgyIndex(-1);
          }}
          onFocus={() => setIsBrgyFocused(true)}
          onBlur={() => setIsBrgyFocused(false)}
          onKeyDown={(e) =>
            handleKeyDown(
              e,
              filteredBarangays,
              (val) => setBrgyValue(val),
              setHighlightedBrgyIndex,
              setIsBrgyFocused
            )
          }
          style={{ borderColor: errors.brgy ? "#da4f4f" : "" }}
        />
        {isBrgyFocused && (
          <div className="nmc-keyword-result" ref={brgyListRef}>
            {filteredBarangays.map((b, idx) => (
              <div
                key={b}
                className={`keyword-result ${
                  idx === highlightedBrgyIndex ? "highlighted" : ""
                }`}
                onMouseDown={() => setBrgyValue(b)}
              >
                {b}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Street */}
      <div className="nmc-input-wrapper">
        <label>House No./ Building no / Street :</label>
        <input
          type="text"
          value={street}
          onChange={(e) => {
            setStreet(e.target.value);
            setErrors((p) => ({ ...p, street: "" }));
          }}
          style={{ borderColor: errors.street ? "#da4f4f" : "" }}
        />
        {errors.street && <span className="error">{errors.street}</span>}
      </div>

      {/* Landmark */}
      <div className="nmc-input-wrapper">
        <label>Landmark (Optional):</label>
        <input
          type="text"
          value={landmark}
          onChange={(e) => setLandmark(e.target.value)}
        />
      </div>

      {/* Checkbox + Buttons */}
      <div className="nmc-action-btn">
        <div className="ncm-chck-set-def">
          <label
            style={{
              cursor: hasAddressAlready ? "pointer" : "default",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
            onClick={(e) => {
              if (!hasAddressAlready) {
                e.preventDefault();
                return;
              }
              setDefaultAddress((prev) => !prev);
            }}
          >
            <input
              type="checkbox"
              readOnly
              checked={hasAddressAlready ? defaultAddress : true}
            />
            <span>SET AS DEFAULT</span>
          </label>

          {!hasAddressAlready && (
            <small className="note">
              Your first address will automatically be set as default.
            </small>
          )}
        </div>

        <button className="ncm-btn-cancel" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="ncm-btn-save"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
}

export default NewAddress;
